import React, { useState, useCallback, useEffect, useContext } from "react";
import AgGrid from "../../framework/AgGrid";
import GlobalContext from "../../framework/GlobalContext";
import SkeletonGrid from "../../framework/skeleton/SkeletonGrid";
import { rowDown, rowUp, selectRow } from "../../utils/gridUtils";
import Highlighter from "react-highlight-words";
import _ from "lodash";
import moment from "moment";
import Checkbox from "../../framework/Checkbox";
import { displayToast } from "../../framework/toast/toastUtils";

const SearchBatchesGrid = (props) => {
  const {
    rowData,
    quickFilterText = "",
    // close = () => {},
    onEscape = () => {},
    onEnter = () => {},
    onTab = () => {},
    addRow = () => {},
    showAddRow = true,
    showAddBatch = () => {},
    selectedIndex = 0,
    showCurrentStock = true,
  } = props;
  const { currentComponent, hasAccess } = useContext(GlobalContext);
  const [api, setApi] = useState(null);
  const [batchRowData, setBatchRowData] = useState(null);
  const [allRowsChecked, setAllRowsChecked] = useState(null);
  const batchSearchKeyupListener = useCallback(
    (event) => {
      if (!api) return;
      const key = event.key?.toLowerCase();
      const ctrlKey = event.ctrlKey;
      if (key === "arrowdown") {
        rowDown(api, ctrlKey);
        return;
      }
      if (key === "arrowup") {
        rowUp(api, ctrlKey);
        return;
      }
      if (key === "tab") {
        onTab(event.shiftKey);
        return;
      }
      if (key === "enter") {
        let checkedBatches = [];
        api.forEachNode((node) => {
          if (node.data.checked) {
            checkedBatches.push(node.data);
          }
        });
        let index = api.getSelectedNodes()[0].rowIndex;
        if (quickFilterText && showAddRow && !index) {
          return addRow();
        }
        if (checkedBatches.length === 0) {
          checkedBatches = [api.getSelectedNodes()[0].data];
        }
        onEnter(checkedBatches);
        return;
      }
      if (ctrlKey && key === "b") {
        if (!hasAccess(["BATCH_CREATE"])) {
          return displayToast({
            type: "error",
            title: "You don't have permission to perform this action.",
          });
        }
        showAddBatch(true);
        return;
      }
      if (key === "escape") {
        onEscape();
        return;
      }
    },
    [
      addRow,
      api,
      hasAccess,
      onEnter,
      onEscape,
      onTab,
      quickFilterText,
      showAddBatch,
      showAddRow,
    ]
  );

  const batchSearchColumnDefs = [
    {
      headerName: "Checked",
      field: "checked",
      minWidth: 30,
      maxWidth: 30,
      cellRendererFramework: (params) => {
        return (
          <>
            {params.data.batchId && (
              <Checkbox
                checked={params.data.checked}
                onChange={(event) => {
                  params.data.checked = event.target.checked;
                  api.redrawRows({
                    rowNodes: [api.getRowNode(params.node.rowIndex)],
                  });
                }}
              />
            )}
          </>
        );
      },
      headerComponentFramework: (params) => {
        return (
          <Checkbox
            checked={allRowsChecked}
            onChange={(event) => {
              api.forEachNode((node) => {
                node.data.checked = event.target.checked;
                api.redrawRows({ rowNodes: [api.getRowNode(node.rowIndex)] });
              });
              setAllRowsChecked(event.target.checked);
            }}
          />
        );
      },
    },
    {
      headerName: "Batch",
      field: "batchNumber",
      minWidth: 300,
      cellRendererFramework: (params) => {
        if (quickFilterText && params.node.rowIndex === 0) {
          return <div>{params.value}</div>;
        } else {
          return (
            <Highlighter
              highlightStyle={{
                backgroundColor: params?.node?.selected ? "white" : "#FDCE57",
                borderRadius: 4,
                //QA Feedback
              }}
              searchWords={quickFilterText
                ?.trim()
                .split(new RegExp(/[^A-Za-z0-9]/))}
              autoEscape={true}
              textToHighlight={params?.value}
            />
          );
        }
      },
    },
    {
      headerName: "Expiry",
      field: "batchExpiry",
      valueGetter: (params) =>
        params.data.batchExpiry
          ? moment(params.data.batchExpiry).format("MM/YY")
          : "",
    },
    // {
    //   headerName: "Margin %",
    //   field: "marginPer",
    //   valueGetter: (params) =>
    //     getMarginPercent(params.data.mrp, params.data.effectiveCostPrice),
    // },
    { headerName: "Stock", field: "currQuantity", hide: !showCurrentStock },
    { headerName: "Loose", field: "currLoose", hide: !showCurrentStock },
    { headerName: "Rack", field: "rack" },
    {
      headerName: "MRP ₹",
      field: "mrp",
      valueFormatter: (params) => params.value?.toFixed(2),
    },
  ];
  const initialLoad = useCallback((rowData) => setBatchRowData(rowData), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceInitialLoad = useCallback(_.debounce(initialLoad, 300), []);
  useEffect(() => {
    if (currentComponent === "grid_multi_batch_search")
      document.addEventListener("keyup", batchSearchKeyupListener, false);
    return () => {
      document.removeEventListener("keyup", batchSearchKeyupListener, false);
    };
  }, [batchSearchKeyupListener, currentComponent]);
  useEffect(() => {
    debounceInitialLoad(rowData);
    return () => {
      debounceInitialLoad.cancel();
    };
  }, [debounceInitialLoad, rowData]);
  return (
    <>
      {!batchRowData && <SkeletonGrid />}
      {batchRowData && (
        <AgGrid
          columnDefs={batchSearchColumnDefs}
          defaultColDef={{
            flex: 1,
            cellStyle: (params) => {
              let color = "black";
              let fontSize = "13px";
              let backgroundColor = "transparent";
              if (showAddRow && quickFilterText && params.rowIndex === 0) {
                fontSize = "16px";
                color = "white";
                backgroundColor = "#434df9";
              }
              return {
                color,
                backgroundColor,
                fontSize,
                fontWeight: 700 /*QA feedback*/,
              };
            },
          }} /*QA feedback*/
          onGridReady={(params) => {
            setApi(params.api);
            params.api.sizeColumnsToFit();
          }}
          onRowDataChanged={(params) => {
            if (params.api.getDisplayedRowCount() > 0) {
              if (showAddRow && quickFilterText) {
                selectRow(params.api, 1);
                return;
              }
              selectRow(params.api, selectedIndex);
            }
          }}
          quickFilterText={quickFilterText}
          rowData={batchRowData}
          getRowClass={(params) => {
            let returnClass = "";
            if (params.data.checked) {
              returnClass += "checked ";
            }
            return returnClass;
          }}
          onFilterChanged={(params) => {
            if (quickFilterText && showAddRow) {
              let addNewBatch = { batchNumber: "+Add " + quickFilterText };
              setBatchRowData([addNewBatch, ...rowData]);
            } else {
              setBatchRowData(rowData);
            }
            if (
              quickFilterText &&
              params.api.getDisplayedRowCount() > 1 &&
              showAddRow
            ) {
              selectRow(params.api, 1);
            } else {
              selectRow(params.api, 0);
            }
          }}
        />
      )}
    </>
  );
};
export default SearchBatchesGrid;
